import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, ShoppingBag, X } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "@/context/CartContext";
import { scrollToId } from "./SmoothScroll";

const NAV = [
  { id: "collection", label: "Shop" },
  { id: "shades", label: "Shade Studio" },
  { id: "gifting", label: "Gifting" },
  { id: "ritual", label: "Ritual" },
];

export function Header() {
  const { count, setOpen } = useCart();
  const [menu, setMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const go = (id) => {
    setMenu(false);
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 400);
    } else scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 border-b transition-colors duration-300 backdrop-blur-xl ${
        scrolled ? "bg-[#FAF7F2]/85 border-[#E6D7CC]" : "bg-[#FAF7F2]/60 border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 h-[68px] flex items-center justify-between">
        <Link to="/" data-testid="header-home-link"><Logo /></Link>
        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => go(n.id)} data-testid={`nav-${n.id}`}
              className="group relative text-sm text-[#4A2E2B] tracking-wide">
              {n.label}
              <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-[#4A2E2B] transition-transform duration-300 group-hover:scale-x-100" />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <motion.button whileTap={{ scale: 0.94 }} onClick={() => setOpen(true)} data-testid="cart-open-btn"
            className="flex items-center gap-2 rounded-full bg-[#281715] text-[#FAF7F2] pl-4 pr-3 h-10 text-sm hover:bg-[#4A2E2B] transition-colors duration-200">
            <ShoppingBag className="w-4 h-4" strokeWidth={1.6} />
            <span className="hidden sm:inline">Bag</span>
            <span data-testid="cart-count" className="grid place-items-center min-w-6 h-6 px-1.5 rounded-full bg-[#EBB1B6] text-[#281715] text-xs font-semibold">
              {count}
            </span>
          </motion.button>
          <button className="md:hidden w-10 h-10 grid place-items-center" onClick={() => setMenu((m) => !m)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {menu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {menu && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#FAF7F2] border-t border-[#E6D7CC]" data-testid="mobile-menu">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => go(n.id)} data-testid={`mobile-nav-${n.id}`}
                className="block w-full text-left px-6 py-4 font-display text-2xl text-[#281715] border-b border-[#E6D7CC]/70">
                {n.label}
              </button>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
